/* eslint-disable no-underscore-dangle, no-param-reassign, class-methods-use-this */
import { Node } from "@xyflow/react";

interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface QuadTreeNode {
  bounds: Rectangle;
  nodes: Node[];
  children: QuadTreeNode[] | null;
}

const MAX_NODES = 4;
const MAX_DEPTH = 8;

export class QuadTree {
  private readonly root: QuadTreeNode;

  constructor(bounds: Rectangle) {
    this.root = {
      bounds,
      nodes: [],
      children: null,
    };
  }

  insert(node: Node, depth = 0): void {
    this._insert(this.root, node, depth);
  }

  private _insert(tree: QuadTreeNode, node: Node, depth: number): void {
    if (!this.intersects(tree.bounds, this.getNodeBounds(node))) {
      return;
    }

    if (tree.children === null) {
      tree.nodes.push(node);

      if (tree.nodes.length > MAX_NODES && depth < MAX_DEPTH) {
        this.subdivide(tree);
        const nodesToRedistribute = [...tree.nodes];
        tree.nodes = [];
        nodesToRedistribute.forEach((n) => this._insert(tree, n, depth));
      }
    } else {
      tree.children.forEach((child) => this._insert(child, node, depth + 1));
    }
  }

  private subdivide(tree: QuadTreeNode): void {
    const { x, y, width, height } = tree.bounds;
    const halfWidth = width / 2;
    const halfHeight = height / 2;

    tree.children = [
      {
        bounds: { x, y, width: halfWidth, height: halfHeight },
        nodes: [],
        children: null,
      },
      {
        bounds: { x: x + halfWidth, y, width: halfWidth, height: halfHeight },
        nodes: [],
        children: null,
      },
      {
        bounds: { x, y: y + halfHeight, width: halfWidth, height: halfHeight },
        nodes: [],
        children: null,
      },
      {
        bounds: {
          x: x + halfWidth,
          y: y + halfHeight,
          width: halfWidth,
          height: halfHeight,
        },
        nodes: [],
        children: null,
      },
    ];
  }

  query(viewport: Rectangle): Node[] {
    const result: Node[] = [];
    this._query(this.root, viewport, result);
    return result;
  }

  private _query(
    tree: QuadTreeNode,
    viewport: Rectangle,
    result: Node[]
  ): void {
    if (!this.intersects(tree.bounds, viewport)) {
      return;
    }

    tree.nodes.forEach((node) => {
      if (this.intersects(this.getNodeBounds(node), viewport)) {
        result.push(node);
      }
    });

    if (tree.children) {
      tree.children.forEach((child) => this._query(child, viewport, result));
    }
  }

  private intersects(a: Rectangle, b: Rectangle): boolean {
    return !(
      a.x + a.width < b.x ||
      b.x + b.width < a.x ||
      a.y + a.height < b.y ||
      b.y + b.height < a.y
    );
  }

  private getNodeBounds(node: Node): Rectangle {
    const width = node.measured?.width || 340;
    const height = node.measured?.height || 150;
    return {
      x: node.position.x,
      y: node.position.y,
      width,
      height,
    };
  }
}
